import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import atlasPlates from '../data/atlas_hn_plates.json';
import { 
  Leaf, X, Info, CheckCircle2, 
  ZoomIn, ZoomOut, RotateCw, Maximize2, Minimize2, 
  RefreshCw, ArrowLeft, Move, Search, BookOpen
} from 'lucide-react';

// Convertisseur Romain <-> Arabe bivalent (Règle 66)
function parsePlateNumber(param) {
  if (!param) return null;
  const s = String(param).trim();
  const directNum = parseInt(s, 10);
  if (!isNaN(directNum) && directNum > 0 && directNum <= 112) {
    return directNum;
  }
  // Décodeur chiffres romains
  const romanMap = { i: 1, v: 5, x: 10, l: 50, c: 100, d: 500, m: 1000 };
  const clean = s.toLowerCase();
  let total = 0;
  for (let i = 0; i < clean.length; i++) {
    const val = romanMap[clean[i]];
    if (!val) { total = 0; break; }
    const nextVal = romanMap[clean[i + 1]] || 0;
    if (val < nextVal) {
      total -= val;
    } else {
      total += val;
    }
  }
  if (total > 0 && total <= 112) return total;
  return null;
}

export default function HistoireNaturelleToc() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Résolution robuste d'une planche à partir du paramètre URL
  const resolvePlateFromParam = (param, figParam = '') => {
    const plateNum = parsePlateNumber(param);
    if (!plateNum) return null;
    const found = atlasPlates.find((x) => x.plate === plateNum);
    if (found) {
      return {
        ...found,
        targetFig: figParam ? String(figParam).trim() : ''
      };
    }
    return null;
  };

  const initialResolved = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const rawPlanche = params.get('planche');
    const rawFig = params.get('fig') || params.get('figure') || '';
    return resolvePlateFromParam(rawPlanche, rawFig);
  }, []);

  const [selected, setSelected] = useState(initialResolved);
  const [activeFig, setActiveFig] = useState(initialResolved ? (initialResolved.targetFig || '') : '');
  const [searchTerm, setSearchTerm] = useState('');

  // Visionneuse interactive : zoom, rotation, pan et plein écran
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const modalRef = useRef(null);

  const fromBible = useMemo(() => {
    return Boolean(
      location.state?.fromBible ||
      searchParams.get('from_bible') === '1' ||
      (searchParams.get('planche') && document.referrer && !document.referrer.includes('/histoire-naturelle'))
    );
  }, [location.state, searchParams]);

  const resetTransforms = () => {
    setZoom(1);
    setRotation(0);
    setPan({ x: 0, y: 0 });
  };

  // Synchronisation avec l'état plein écran du navigateur
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleBrowserFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        if (modalRef.current?.requestFullscreen) {
          await modalRef.current.requestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
      }
    } catch (err) {
      console.warn('Erreur lors du basculement plein écran :', err);
    }
  };

  // Écoute de l'URL pour ouvrir directement une planche (ex: /histoire-naturelle?planche=5&fig=2)
  useEffect(() => {
    const pParam = searchParams.get('planche');
    const fParam = searchParams.get('fig') || searchParams.get('figure') || '';
    if (pParam) {
      const match = resolvePlateFromParam(pParam, fParam);
      if (match) {
        setSelected(match);
        setActiveFig(match.targetFig || fParam);
        resetTransforms();
      }
    }
  }, [searchParams]);

  const handleOpenModal = (p, figNum = '') => {
    setSelected({ ...p, targetFig: figNum ? String(figNum) : '' });
    setActiveFig(figNum ? String(figNum) : '');
    resetTransforms();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('planche', p.roman);
    if (figNum) {
      nextParams.set('fig', String(figNum));
    } else {
      nextParams.delete('fig');
      nextParams.delete('figure');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleCloseModal = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    setSelected(null);
    setActiveFig('');
    resetTransforms();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('planche');
    nextParams.delete('fig');
    nextParams.delete('figure');
    setSearchParams(nextParams, { replace: true });
  };

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.35, 4));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.35, 0.6));
  const handleRotate = () => setRotation((r) => (r + 90) % 360);

  // Déplacement souris (Pan)
  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setPan({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Déplacement tactile (Touch pan)
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      dragStartRef.current = {
        x: e.touches[0].clientX - pan.x,
        y: e.touches[0].clientY - pan.y
      };
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    setPan({
      x: e.touches[0].clientX - dragStartRef.current.x,
      y: e.touches[0].clientY - dragStartRef.current.y
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Filtrage des planches par texte
  const filteredPlates = useMemo(() => {
    if (!searchTerm.trim()) return atlasPlates;
    const term = searchTerm.toLowerCase();
    return atlasPlates.filter((p) => {
      if (p.roman.toLowerCase().includes(term) || String(p.plate).includes(term)) return true;
      if (p.title && p.title.toLowerCase().includes(term)) return true;
      if (p.figures && p.figures.some((f) => f.desc && f.desc.toLowerCase().includes(term))) return true;
      return false;
    });
  }, [searchTerm]);

  return (
    <div className="min-h-screen bg-paper pb-16">
      {/* En-tête */}
      <div className="bg-paper-dark border-b border-border py-8 px-4 sm:px-6 mb-8">
        <div className="max-w-6xl mx-auto">
          {fromBible && (
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-lg text-sm font-medium text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au texte biblique
            </button>
          )}

          <div className="flex items-center gap-3 mb-2">
            <span className="p-2.5 rounded-xl bg-emerald-100/80 text-emerald-800 border border-emerald-200 shadow-sm">
              <Leaf className="w-6 h-6" />
            </span>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl text-ink-primary font-bold">
                Atlas d'Histoire Naturelle de la Bible
              </h1>
              <p className="text-xs sm:text-sm text-ink-secondary mt-0.5">
                Flore, Faune, Géologie et Ethnologie bibliques — L.-Cl. Fillion (1884) • 112 Planches Gravées
              </p>
            </div>
          </div>

          {/* Barre de recherche */}
          <div className="mt-6 max-w-md relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-secondary" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher une plante, un animal, une planche..."
              className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-700/20 focus:border-emerald-700"
            />
          </div>
        </div>
      </div>

      {/* Grille des planches */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredPlates.map((p) => (
            <div
              key={p.plate}
              onClick={() => handleOpenModal(p)}
              className="group bg-white rounded-xl border border-border shadow-sm hover:shadow-md hover:border-emerald-600 transition-all duration-200 overflow-hidden cursor-pointer flex flex-col"
            >
              {/* Image Miniature */}
              <div className="aspect-[4/3] bg-paper-dark relative overflow-hidden flex items-center justify-center p-2">
                <img
                  src={`/atlas_hn/${p.thumb || p.file}`}
                  alt={`Planche ${p.roman}`}
                  loading="lazy"
                  className="max-h-full max-w-full object-contain filter group-hover:contrast-105 transition-transform duration-300 group-hover:scale-105"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `/atlas_hn/${p.file}`;
                  }}
                />
                <span className="absolute top-2 left-2 px-2 py-0.5 bg-paper/90 backdrop-blur-sm border border-border text-[11px] font-serif font-bold text-ink-primary rounded">
                  Pl. {p.roman}
                </span>
              </div>

              {/* Contenu textuel */}
              <div className="p-3.5 flex-1 flex flex-col">
                <h3 className="font-heading text-sm font-semibold text-ink-primary line-clamp-2 group-hover:text-emerald-800 transition-colors">
                  {p.title || `Planche ${p.roman}`}
                </h3>
                {p.figures && p.figures.length > 0 && (
                  <p className="text-[11px] text-ink-secondary mt-1">
                    {p.figures.length} figure{p.figures.length > 1 ? 's' : ''} gravée{p.figures.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MODALE VISIONNEUSE INTERACTIVE */}
      {selected && (
        <div
          ref={modalRef}
          className="fixed inset-0 z-50 bg-black/90 flex flex-col backdrop-blur-sm select-none"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Barre supérieure de contrôle */}
          <div className="h-14 bg-paper/95 border-b border-border px-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-3">
              <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 font-serif font-bold text-sm rounded">
                Planche {selected.roman}
              </span>
              <h2 className="text-sm font-heading font-medium text-ink-primary truncate max-w-[200px] sm:max-w-md">
                {selected.title}
              </h2>
            </div>

            {/* Outils de zoom, rotation, plein écran et fermeture */}
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={handleZoomIn}
                title="Agrandir"
                className="p-2 rounded-lg text-ink-secondary hover:text-ink-primary hover:bg-paper-dark transition-colors"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              <button
                onClick={handleZoomOut}
                title="Réduire"
                className="p-2 rounded-lg text-ink-secondary hover:text-ink-primary hover:bg-paper-dark transition-colors"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <button
                onClick={handleRotate}
                title="Pivoter de 90°"
                className="p-2 rounded-lg text-ink-secondary hover:text-ink-primary hover:bg-paper-dark transition-colors"
              >
                <RotateCw className="w-5 h-5" />
              </button>
              <button
                onClick={resetTransforms}
                title="Réinitialiser vue"
                className="p-2 rounded-lg text-ink-secondary hover:text-ink-primary hover:bg-paper-dark transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={toggleBrowserFullscreen}
                title={isFullscreen ? "Quitter plein écran" : "Plein écran"}
                className="p-2 rounded-lg text-ink-secondary hover:text-ink-primary hover:bg-paper-dark transition-colors"
              >
                {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </button>
              <div className="h-6 w-[1px] bg-border mx-1" />
              <button
                onClick={handleCloseModal}
                title="Fermer (Échap)"
                className="p-2 rounded-lg text-ink-secondary hover:text-rubric-red hover:bg-paper-dark transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Espace central de l'image (avec Pan & Zoom interactifs) */}
          <div 
            className="flex-1 relative overflow-hidden flex items-center justify-center cursor-grab active:cursor-grabbing p-4"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <img
              src={`/atlas_hn/${selected.file}`}
              alt={`Planche ${selected.roman}`}
              draggable={false}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                maxWidth: '92%',
                maxHeight: '92%',
                objectContain: 'contain'
              }}
              className="rounded shadow-2xl pointer-events-none select-none"
            />

            {/* Pastille indiquant le ciblage d'une figure */}
            {activeFig && (
              <div className="absolute top-4 left-4 z-20 px-3.5 py-1.5 bg-emerald-800/90 text-white backdrop-blur-md rounded-full shadow-lg border border-emerald-600/40 flex items-center gap-2 animate-fade-in text-xs font-medium">
                <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                <span>Figure {activeFig} ciblée dans le texte</span>
              </div>
            )}
          </div>

          {/* Volet inférieur des figures de la planche */}
          {selected.figures && selected.figures.length > 0 && (
            <div className="bg-paper/95 border-t border-border p-3 max-h-48 overflow-y-auto">
              <div className="max-w-5xl mx-auto">
                <p className="text-[11px] font-semibold text-ink-secondary uppercase tracking-wider mb-2">
                  Figures représentées sur la planche {selected.roman} :
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.figures.map((f) => {
                    const isTarget = String(activeFig) === String(f.fig);
                    return (
                      <button
                        key={f.fig}
                        onClick={() => setActiveFig(String(f.fig))}
                        className={`text-left text-xs px-2.5 py-1.5 rounded-lg border transition-all ${
                          isTarget
                            ? 'bg-emerald-50 border-emerald-500 text-emerald-900 font-semibold shadow-sm'
                            : 'bg-paper-dark/60 border-border text-ink-primary hover:bg-paper-dark'
                        }`}
                      >
                        <span className="font-bold mr-1">Fig. {f.fig} :</span>
                        <span>{f.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
